<script>
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import LoadingState from '../../../components/widgets/LoadingState.vue';
import ProjectCard from './ProjectCard.vue';
import ProjectsTable from './ProjectsTable.vue';
import RealEstateTabs from './RealEstateTabs.vue';
import OverviewModal from './modals/OverviewModal.vue';
import BulkImportModal from './modals/BulkImportModal.vue';
import {
  PROP_TYPES,
  STATUSES,
  PRICE_TIERS,
  FIELD_CLASS,
  SELECT_CLASS,
} from './constants';
import { listProperties } from '../../../api/realEstate/properties';

// Filters are sent to GET /properties as query params (name/area search,
// property_type, region, project_status, min_price/max_price, active) —
// the server does the filtering now, not a client-side .filter() over a
// locally-held rollup (docs/real-estate/be/07-fe-handoff.md §5 "Properties
// list"). Search is debounced so it doesn't re-query on every keystroke.
const SEARCH_DEBOUNCE_MS = 300;

// Multiples of 3 so the card grid's last row is full.
const PAGE_SIZE = 24;
const PAGE_SIZE_OPTIONS = [12, 24, 48];

const VIEW_STORAGE_KEY = 're.projectsView';

function readStoredView() {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === 'cards' || stored === 'table') return stored;
  } catch (e) {
    // localStorage can throw (private browsing, disabled storage) — fall
    // through to the default below.
  }
  return 'cards';
}

export default {
  name: 'ProjectsList',
  components: {
    PageHeader,
    PaginationBar,
    LoadingState,
    ProjectCard,
    ProjectsTable,
    RealEstateTabs,
    OverviewModal,
    BulkImportModal,
  },
  data() {
    return {
      isLoading: false,
      isCreateModalOpen: false,
      isBulkImportModalOpen: false,
      viewMode: readStoredView(),
      searchQuery: '',
      // Trails searchQuery by SEARCH_DEBOUNCE_MS; listQuery reads this one
      // so typing doesn't refetch on every keystroke.
      debouncedSearch: '',
      filterType: '',
      filterRegion: '',
      filterStatus: '',
      filterPriceTier: '',
      filterActive: '',
      propTypes: PROP_TYPES,
      statuses: STATUSES,
      priceTiers: PRICE_TIERS,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      searchDebounceTimer: null,
      page: 1,
      pageSize: PAGE_SIZE,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      total: 0,
      // Inactive projects matching the current filters, across all pages.
      inactiveCount: 0,
    };
  },
  computed: {
    ...mapGetters('realEstate', {
      projects: 'getProjects',
      regions: 'getRegions',
    }),
    listQuery() {
      const query = {};
      if (this.debouncedSearch) query.q = this.debouncedSearch;
      if (this.filterType) query.property_type = this.filterType;
      if (this.filterRegion) query.region = this.filterRegion;
      if (this.filterStatus) query.project_status = this.filterStatus;
      if (this.filterActive) query.active = this.filterActive;
      if (this.filterPriceTier) {
        const [lo, hi] = this.filterPriceTier.split('-');
        query.min_price = lo;
        if (hi) query.max_price = hi;
      }
      return query;
    },
    hasActiveFilters() {
      return Boolean(
        this.searchQuery.trim() ||
          this.filterType ||
          this.filterRegion ||
          this.filterStatus ||
          this.filterPriceTier ||
          this.filterActive
      );
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: Math.max(1, Math.ceil(this.total / this.pageSize)),
        pageSize: this.pageSize,
      };
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearch = this.searchQuery.trim();
      }, SEARCH_DEBOUNCE_MS);
    },
    // One watcher for every filter, so clearing several at once refetches
    // once rather than once per filter. A filter change goes back to page 1;
    // when that changes `page`, its own watcher does the load.
    listQuery() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.loadProjects();
      }
    },
    page() {
      this.loadProjects();
    },
  },
  mounted() {
    this.loadProjects();
    this.fetchRegions();
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    ...mapActions('realEstate', ['fetchProjects', 'fetchRegions']),
    async loadProjects() {
      this.isLoading = true;
      try {
        const [data, inactiveCount] = await Promise.all([
          this.fetchProjects({
            ...this.listQuery,
            page: this.page,
            page_size: this.pageSize,
          }),
          this.countInactive(),
        ]);
        this.total = data.total;
        this.inactiveCount = inactiveCount;
        // A delete elsewhere can leave this page past the end — step back.
        if (!data.results.length && this.page > 1) {
          this.page = Math.max(1, Math.ceil(data.total / this.pageSize));
        }
      } finally {
        this.isLoading = false;
      }
      if (this.$refs.listBody) this.$refs.listBody.scrollTop = 0;
    },
    // Only the total is needed, so one row is enough.
    async countInactive() {
      if (this.filterActive === 'active') return 0;
      const { data } = await listProperties({
        ...this.listQuery,
        active: 'inactive',
        page_size: 1,
      });
      return data.total;
    },
    handlePageSizeChange(size) {
      this.pageSize = size;
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.loadProjects();
      }
    },
    clearFilters() {
      clearTimeout(this.searchDebounceTimer);
      this.searchQuery = '';
      this.debouncedSearch = '';
      this.filterType = '';
      this.filterRegion = '';
      this.filterStatus = '';
      this.filterPriceTier = '';
      this.filterActive = '';
    },
    setViewMode(mode) {
      this.viewMode = mode;
      try {
        window.localStorage.setItem(VIEW_STORAGE_KEY, mode);
      } catch (e) {
        // localStorage can throw (private browsing, disabled storage) —
        // the view choice just won't persist across reloads.
      }
    },
  },
};
</script>

<template>
  <div class="p-4 md:p-8 h-full flex flex-col overflow-hidden flex-1 min-w-0">
    <OverviewModal
      v-if="isCreateModalOpen"
      @close="isCreateModalOpen = false"
    />
    <BulkImportModal
      v-if="isBulkImportModalOpen"
      @close="isBulkImportModalOpen = false"
      @imported="loadProjects"
    />

    <PageHeader
      :title="$t('REAL_ESTATE.LIST.HEADER_TITLE')"
      icon="condo"
      :description="$t('REAL_ESTATE.LIST.HEADER_DESCRIPTION')"
    >
      <template #actions>
        <woot-button
          variant="clear"
          icon="upload"
          @click="isBulkImportModalOpen = true"
        >
          {{ $t('REAL_ESTATE.LIST.BULK_IMPORT_BUTTON') }}
        </woot-button>
        <woot-button icon="add" @click="isCreateModalOpen = true">
          {{ $t('REAL_ESTATE.LIST.NEW_PROJECT_BUTTON') }}
        </woot-button>
      </template>
    </PageHeader>

    <RealEstateTabs class="mb-6" />

    <div
      class="mt-4 flex flex-col md:flex-row flex-wrap gap-3 items-stretch md:items-center"
    >
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="$t('REAL_ESTATE.LIST.SEARCH_PLACEHOLDER')"
        :class="fieldClass"
        class="max-w-xs mb-0 reset-base"
      />
      <select v-model="filterType" :class="selectClass" class="max-w-[180px]">
        <option value="">{{ $t('REAL_ESTATE.LIST.FILTER_ALL_TYPES') }}</option>
        <option v-for="type in propTypes" :key="type" :value="type">
          {{ type }}
        </option>
      </select>
      <select v-model="filterRegion" :class="selectClass" class="max-w-[200px]">
        <option value="">
          {{ $t('REAL_ESTATE.LIST.FILTER_ALL_LOCATIONS') }}
        </option>
        <option v-for="region in regions" :key="region" :value="region">
          {{ region }}
        </option>
      </select>
      <select v-model="filterStatus" :class="selectClass" class="max-w-[200px]">
        <option value="">
          {{ $t('REAL_ESTATE.LIST.FILTER_ALL_STATUSES') }}
        </option>
        <option v-for="(label, key) in statuses" :key="key" :value="key">
          {{ label }}
        </option>
      </select>
      <select
        v-model="filterPriceTier"
        :class="selectClass"
        class="max-w-[200px]"
      >
        <option
          v-for="[value, label] in priceTiers"
          :key="value"
          :value="value"
        >
          {{ label }}
        </option>
      </select>
      <select v-model="filterActive" :class="selectClass" class="max-w-[190px]">
        <option value="">{{ $t('REAL_ESTATE.LIST.FILTER_ALL_ACTIVE') }}</option>
        <option value="active">
          {{ $t('REAL_ESTATE.LIST.FILTER_ACTIVE_ONLY') }}
        </option>
        <option value="inactive">
          {{ $t('REAL_ESTATE.LIST.FILTER_INACTIVE_ONLY') }}
        </option>
      </select>
    </div>

    <!-- Own row so nothing shifts: the view toggle is fixed on the left,
         Clear filters appears right after it, and the inactive count
         sits on the right. -->
    <div class="mt-3 flex items-center gap-3">
      <div
        class="flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-700 p-1 shrink-0"
      >
        <button
          type="button"
          class="px-3 py-1 rounded-md text-sm"
          :class="
            viewMode === 'cards'
              ? 'bg-white dark:bg-slate-500 shadow-sm font-semibold text-slate-900 dark:text-slate-100'
              : 'text-slate-500 dark:text-slate-300'
          "
          @click="setViewMode('cards')"
        >
          {{ $t('REAL_ESTATE.LIST.VIEW_CARDS') }}
        </button>
        <button
          type="button"
          class="px-3 py-1 rounded-md text-sm"
          :class="
            viewMode === 'table'
              ? 'bg-white dark:bg-slate-500 shadow-sm font-semibold text-slate-900 dark:text-slate-100'
              : 'text-slate-500 dark:text-slate-300'
          "
          @click="setViewMode('table')"
        >
          {{ $t('REAL_ESTATE.LIST.VIEW_TABLE') }}
        </button>
      </div>
      <woot-button
        v-if="hasActiveFilters"
        variant="clear"
        color-scheme="secondary"
        size="small"
        icon="dismiss"
        class="shrink-0"
        @click="clearFilters"
      >
        {{ $t('REAL_ESTATE.LIST.CLEAR_FILTERS') }}
      </woot-button>
      <span
        v-if="total"
        class="ml-auto text-xs text-slate-400 dark:text-slate-500"
      >
        {{ $tc('REAL_ESTATE.LIST.TOTAL_COUNT', total, { count: total }) }}
        <template v-if="inactiveCount">
          ·
          {{ $t('REAL_ESTATE.LIST.INACTIVE_COUNT', { count: inactiveCount }) }}
        </template>
      </span>
    </div>

    <div ref="listBody" class="mt-4 flex-1 overflow-y-auto">
      <LoadingState
        v-if="isLoading"
        :message="$t('REAL_ESTATE.LIST.LOADING')"
      />
      <div
        v-else-if="projects.length === 0"
        class="flex flex-col items-center justify-center py-12"
      >
        <fluent-icon
          icon="condo"
          class="text-slate-300 dark:text-slate-600 mb-4"
          size="48"
        />
        <p class="text-slate-500 dark:text-slate-400 text-center">
          {{ $t('REAL_ESTATE.LIST.EMPTY_STATE') }}
        </p>
        <woot-button
          v-if="hasActiveFilters"
          variant="smooth"
          size="small"
          class="mt-4"
          @click="clearFilters"
        >
          {{ $t('REAL_ESTATE.LIST.CLEAR_FILTERS') }}
        </woot-button>
      </div>
      <div
        v-else-if="viewMode === 'cards'"
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
      >
        <ProjectCard
          v-for="project in projects"
          :key="project.property_id"
          :project="project"
        />
      </div>
      <ProjectsTable v-else :projects="projects" />

      <PaginationBar
        v-if="!isLoading && total > pageSizeOptions[0]"
        :pagination="pagination"
        :loading="isLoading"
        :page-size-options="pageSizeOptions"
        @previous="page -= 1"
        @next="page += 1"
        @page-size-change="handlePageSizeChange"
      />
    </div>
  </div>
</template>
